import React,{ FC }         from 'react';
import { Navigate }         from 'react-router';
import {
    useLocation,
    useNavigate
}                           from 'react-router-dom';

import consts               from 'shared/consts';
import * as VehicleType     from 'shared/types/Vehicle';
import * as DVIRecordType   from 'shared/types/DVIRecord';
import * as InventoryItemType   from 'shared/types/InventoryItem';

import tectransit           from 'utils/TecTransit';
import getApiPromise        from 'utils/getApiPromise';

import * as MenuItem        from 'components/MenuItem';
import * as DVISTableType   from 'Maintenance/components/DVISTable';

interface Props {
}

const NewRepair : FC<Props> = props => {

    const location       = useLocation();
    const navigate       = useNavigate();
    const vehicle        = location.state.vehicle as VehicleType.Vehicle;
    const [
        redirectToVehicles,
        setRedirectToVehicles]          = React.useState<boolean>(false);
    const [dvis,setDVIs]                = React.useState<DVISTableType.DVIType[]|undefined>();
    const [errMessage,setErrMessage]    = React.useState<string>('');
    const [workItems,setWorkItems]      = React.useState<DVIRecordType.RepairWorkItem[]>([]);
    const [
        repairPartItems,
        setRepairPartItems]             = React.useState<DVIRecordType.RepairPartItem[]>([]);
    const [
        inventoryItems,
        setInventoryItems]              = React.useState<InventoryItemType.InventoryItem[]>([]);
    const [
        selectedInventoryItemId,
        setSelectedInventoryItemId]     = React.useState<string>('');

    const selectedInventoryItem         = inventoryItems.find(item => item._id===selectedInventoryItemId);

    React.useEffect(() => {
        getApiPromise<DVISTableType.DVIType[]>('/api/maintenance/vehicle/dvis','GET',{},{
            vehicle_id  : vehicle._id,
            repair_id   : null,
            orderby     : 'ms desc',
            limit       : 1000
        }).then( dvis => {
            if( !dvis || dvis.err )
                throw Error(dvis?.err||`dvis are empty`);
            // auto-select the first DVI, if there is only one
            if( dvis.length===1 )
                dvis[0].selected = true;
            setDVIs(dvis);
        })
        .catch( err => {
            setErrMessage(`Cannot get DVIs (${err.message})`);
        });
    },[vehicle._id]);

    React.useEffect(() => {
        getApiPromise<InventoryItemType.InventoryItem[]>('/api/maintenance/inventory','GET',{},{
            orderby     : 'partNumber desc',
            limit       : 1000
        }).then( inventoryItems => {
            if( !inventoryItems || inventoryItems.err )
                throw Error(inventoryItems?.err||`inventory items are empty`);
            setInventoryItems(inventoryItems);
        })
        .catch( err => {
            setErrMessage(`Cannot get inventory items (${err.message})`);
        });
    },[]);

    if( redirectToVehicles )
        return (<Navigate to={'/Maintenance/Vehicles'}/>);

    const formInput = React.createRef() as React.RefObject<HTMLFormElement>;
    const getWorkItem = () : DVIRecordType.RepairWorkItem => {
        const elements = formInput.current?.elements;
        if( !elements )
            throw Error(`Cannot find form elements`);
        const description = elements.namedItem('wi_description') as HTMLInputElement;
        const hours       = elements.namedItem('wi_hours') as HTMLInputElement;
        if( !description || !hours )
            throw Error('Cannot find form description or hours');
        description.value = description.value.trim();
        if( !description.value )
            throw Error('Description should be provided');
        hours.value = hours.value.trim();
        if( !/^\d+(\.\d+)?$/.test(hours.value) )
            throw Error('Hours should be a number');
        return {
            description : description.value,
            hours       : Number(hours.value)
        };
    };
    const getPartItem = () : DVIRecordType.RepairPartItem => {
        const elements = formInput.current?.elements;
        if( !elements )
            throw Error(`Cannot find form elements`);
        const qty           = elements.namedItem('pi_qty') as HTMLInputElement;
        const partNumber    = selectedInventoryItem?.partNumber;
        const description   = elements.namedItem('pi_description') as HTMLInputElement;
        const cost          = elements.namedItem('pi_cost') as HTMLInputElement;
        if( !qty || !partNumber || !description || !cost )
            throw Error(`Cannot find qty, part, description or cost`);
        qty.value = qty.value.trim();
        if( !/^\d+$/.test(qty.value) )
            throw Error('Quantity should be a number');
        if( !/^[#0-9a-zA-Z].*$/.test(partNumber) )
            throw Error('Part # should be provided');
        if( repairPartItems.some(p=>p.part===partNumber) )
            throw Error(`Part #${partNumber} already exists`);
        cost.value = cost.value.trim();
        if( !/^\$?\d+(\.\d+)?$/.test(cost.value) )
            throw Error('Cost should be a number');
        return{
            qty         : Number(qty.value),
            part        : partNumber,
            description : description.value,
            cost        : Number(cost.value)
        };
    };
    return MenuItem.withMenuItem(`New Repair for Vehicle #${vehicle._id}`, (alert) => {
        if( errMessage ) {
            alert.set(errMessage);
            return false;
        }
        if( !dvis )
            return (<div>Loading...</div>);
        return [
            <DVISTableType.DVISTable
                key         = {'dvisTable'}
                vehicle     = {vehicle}
                dvis        = {dvis}
                navigate    = {navigate}
                selectable  = {true}
            />,
            <form key={'form'} ref={formInput}>
                <table className="wrapper">
                    <tbody>
                        <tr>
                            <td><strong>Fleet #:</strong></td>
                            <td>{vehicle._id}</td>
                        </tr>
                        <tr>
                            <td><strong>Odometer:</strong></td>
                            <td>{Number(vehicle.odometer_meters/consts.meters_in_mile).toFixed(2)} miles</td>
                        </tr>
                        <tr>
                            <td><strong>License Plate:</strong></td>
                            <td>{vehicle.license_plate}, {vehicle.license_state}</td>
                        </tr>
                        <tr>
                            <td><strong>Make, Model, Year:</strong></td>
                            <td>{vehicle.make_model_year}</td>
                        </tr>
                        <tr>
                            <td><strong>Reported Problem:</strong></td>
                            <td>{vehicle.unavailability_reason}</td>
                        </tr>
                        <tr>
                            <td><strong>Other Comments</strong></td>
                            <td>
                                <input
                                    name        ="repairComment"
                                    type        ="text"
                                    defaultValue=""
                                    size        ={50}
                                    placeholder ="enter the repair comment"
                                    maxLength   ={80}
                                    className="input-theme input-theme-min"/>
                            </td>
                        </tr>
                        <tr>
                            <td valign='top'><strong>Work Performed:</strong></td>
                            <td>
                                <table>
                                    <tbody>
                                    <tr>
                                        <td><strong>Description</strong></td>
                                        <td><strong>Hours</strong></td>
                                        <td></td>
                                    </tr>
                                    {workItems.map((wi,wiNdx) => (
                                        <tr key={wiNdx}>
                                            <td>{wi.description}</td>
                                            <td>{wi.hours}</td>
                                            <td>
                                                <button
                                                    onClick={(e)=> {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setWorkItems(workItems.filter((_,ndx)=>ndx!==wiNdx));
                                                    }}
                                                >
                                                -
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    <tr>
                                        <td>
                                            <input
                                                key         = {`wi_description_${workItems.length}`}
                                                name        = "wi_description"
                                                type        = "text"
                                                defaultValue= ""
                                                size        = {50}
                                                className="input-theme input-theme-min"/>
                                        </td>
                                        <td>
                                            <input
                                                key         = {`wi_hours_${workItems.length}`}
                                                name        = "wi_hours"
                                                type        = "number"
                                                min         = {0}
                                                defaultValue= ""
                                                size        = {5}
                                                className="input-theme input-theme-min"/>
                                        </td>
                                        <td>
                                            <button
                                                onClick={(e)=> {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    try {
                                                        setWorkItems([...workItems,getWorkItem()]);
                                                    }
                                                    catch( err ) {
                                                        alert.set(`Cannot add work item (${(err as Error).message})`);
                                                    }
                                                }}
                                            >+
                                            </button>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                        <tr>
                            <td valign='top'><strong>Parts and Accessories:</strong></td>
                            <td>
                                <table>
                                    <tbody>
                                    <tr>
                                        <td><strong>Part #</strong></td>
                                        <td><strong>Description</strong></td>
                                        <td><strong>Cost</strong></td>
                                        <td><strong>QTY.</strong></td>
                                        <td></td>
                                    </tr>
                                    {repairPartItems.map((part,partNdx) => (
                                        <tr key={partNdx}>
                                            <td>{part.part}</td>
                                            <td>{part.description}</td>
                                            <td>{part.cost}</td>
                                            <td>{part.qty}</td>
                                            <td>
                                                <button
                                                    onClick={(e)=> {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        setRepairPartItems(repairPartItems.filter((_,ndx)=>ndx!==partNdx));
                                                    }}
                                                >
                                                -
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                    <tr>
                                    <td>
                                        <select
                                            className   = "input-theme input-theme-min"
                                            key         = {`pi_part_${repairPartItems.length}`}
                                            name        = "pi_part"
                                            value       = {selectedInventoryItemId}
                                            onChange    = {(e) => setSelectedInventoryItemId(e.target.value)}
                                        >
                                            <option value="">Select part</option>
                                            {inventoryItems.map(item => (
                                                <option key={item._id} value={item._id}>
                                                    {item.partNumber}
                                                </option>
                                            ))}
                                        </select>
                                    </td>

                                    <td>
                                        <input
                                            className       = "input-theme input-theme-min"
                                            key             = {`pi_description_${repairPartItems.length}`}
                                            name            = "pi_description"
                                            type            = "text"
                                            value           = {selectedInventoryItem ? selectedInventoryItem.description : ''}
                                            size            = {30}
                                            readOnly
                                        />
                                    </td>

                                    <td>
                                        <input
                                            className       = "input-theme input-theme-min"
                                            key             = {`pi_cost_${repairPartItems.length}`}
                                            name            = "pi_cost"
                                            type            = "text"
                                            value           = {
                                                selectedInventoryItem
                                                    ? (selectedInventoryItem.pricePaid + selectedInventoryItem.taxPaid).toFixed(2)
                                                    : ''
                                            }
                                            size        = {10}
                                            readOnly
                                        />
                                    </td>

                                        <td>
                                            <input
                                                className   = "input-theme input-theme-min"
                                                key         = {`pi_qty_${repairPartItems.length}`}
                                                name        = "pi_qty"
                                                type        = "number"
                                                min         = {0}
                                                max         = {selectedInventoryItem?.quantity||100}
                                                defaultValue= ""
                                                size        = {8}
                                            />
                                        </td>
                                        <td>
                                            <button
                                                onClick     = {(e)=> {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    try {
                                                        setRepairPartItems([...repairPartItems,getPartItem()]);
                                                    }
                                                    catch( err ) {
                                                        alert.set(`Cannot add part item (${(err as Error).message})`);
                                                    }
                                                }}
                                            >+
                                            </button>
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={2}>
                                <br/>
                                <button
                                    className="btn-theme"
                                    onClick={(e)=> {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        const form = (Array.from(formInput.current?.elements||[]) as Record<string,any>[])
                                            .filter( input => {
                                                return (input.type!=='submit');
                                            })
                                            .reduce( (acc,input) => {
                                                acc[input.name] = (input.type==='checkbox') ? input.checked : input.value.trim();
                                                return acc;
                                            },{} as Record<string,any>);
                                        const tmpWorkItems = [...workItems];
                                        try {
                                            tmpWorkItems.push(getWorkItem());
                                            setWorkItems(tmpWorkItems);
                                        }
                                        catch( err ) {
                                            // suppress
                                        }
                                        const tmpPartItems = [...repairPartItems];
                                        try {
                                            tmpPartItems.push(getPartItem());
                                            setRepairPartItems(tmpPartItems);
                                        }
                                        catch( err ) {
                                            // suppress
                                        }
                                        const repair = {
                                            _id             : '',
                                            comment         : form.repairComment,
                                            workItems       : tmpWorkItems,
                                            partItems       : tmpPartItems,
                                            user_id         : tectransit.user._id,
                                            vehicle_id      : vehicle._id,
                                            dvis            : dvis?.filter(dvi=>dvi.selected).map((dvi:any)=>dvi._id)
                                        };
                                        if( repair.dvis.length===0 ) {
                                            if( !window.confirm('No DVIs selected. Do you want to continue?') )
                                                return;
                                        }
                                        if( repair.workItems.length===0 ) {
                                            if( !window.confirm('No work items provided. Do you want to continue?') )
                                                return;
                                        }
                                        if( repair.partItems.length===0 ) {
                                            if( !window.confirm('No parts provided. Do you want to continue?') )
                                                return;
                                        }
                                        const result = DVIRecordType.isRepairValid(repair);
                                        if( result!=='' ) {
                                            alert.set(result);
                                            return;
                                        }
                                        getApiPromise('/api/maintenance/repair','POST',repair)
                                            .then( res => {
                                                if( !res || res.err )
                                                    throw Error(res?.err||'result is empty');
                                                setRedirectToVehicles(true);
                                            })
                                            .catch( err => {
                                                alert.set(`Cannot save (${err.message})`);
                                            });
                                    }}
                                >
                                    Create Repair Record
                                </button>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </form>
        ];
    });
}

export default NewRepair;
